import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import type {
  ThematicPrompt,
  PromptStep,
} from '@/types/thematicMaps';

// Od migracji prompts_no_versioning (2026-08-23): 1 wiersz = 1 step (UNIQUE(step)),
// wszystkie wiersze aktywne. Koniec logiki wersji — createPrompt (dezaktywacja starej
// + INSERT version+1) usunięty; nie miał zresztą żadnego call-site'u.
// updated_at bumpuje trigger DB (trg_thematic_prompts_updated_at), nie klient.
export const useThematicPrompts = () => {
  const [prompts, setPrompts] = useState<ThematicPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchPrompts = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      // Globalne prompty — wzorzec instructions (BEZ user_id filter)
      const { data, error } = await (supabase as any)
        .from('thematic_prompts')
        .select('*')
        .order('step', { ascending: true });

      if (error) throw error;
      setPrompts((data || []) as ThematicPrompt[]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd pobierania promptów';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [user?.id, toast]);

  const getPrompt = useCallback((step: PromptStep): ThematicPrompt | undefined => {
    return prompts.find(p => p.step === step);
  }, [prompts]);

  const updatePrompt = useCallback(async (
    id: string,
    updates: Partial<Pick<ThematicPrompt, 'prompt_template' | 'model' | 'model_id'>>
  ): Promise<boolean> => {
    try {
      const { error } = await (supabase as any)
        .from('thematic_prompts')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setPrompts(prev => prev.map(p =>
        p.id === id ? { ...p, ...updates, updated_at: new Date().toISOString() } : p
      ));
      toast({ title: 'Sukces', description: 'Prompt zaktualizowany' });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd aktualizacji promptu';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return false;
    }
  }, [toast]);

  useEffect(() => {
    if (user?.id) {
      fetchPrompts();
    }
  }, [user?.id, fetchPrompts]);

  return {
    prompts,
    loading,
    getPrompt,
    updatePrompt,
  };
};
